"use client";

import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import FullCalendarBase from "@fullcalendar/react";
import type { CalendarApi, CalendarOptions, DatesSetArg, ViewMountArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from "date-fns";
// FullCalendar CSS is handled in globals.css
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { ChevronLeft, ChevronRight, CalendarDays, Calendar as CalendarIcon, Clock } from "lucide-react";
import { CalendarEvent } from "../../types/calendar";
import { EventDetailsModal } from "./EventDetailsModal";
import { useCalendarContext, CalendarViewType, useSourceColors } from "../../lib/calendar-context";
import { useToast } from "../../hooks/use-toast";
import { useDeleteEvent } from "../../hooks/use-delete-event";
import { useMediaQuery } from "../../hooks/use-media-query";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../alert-dialog";
import { objectsToCSV, downloadCSV } from "../../lib/csv-export";
import Link from "next/link";

interface FullCalendarHandle {
  getApi(): CalendarApi;
}

/** React 19 JSX types reject @fullcalendar/react's class component export. */
const FullCalendar = FullCalendarBase as React.ComponentType<
  CalendarOptions & React.RefAttributes<FullCalendarHandle>
>;

interface AdvancedCalendarProps {
  events: CalendarEvent[];
  isLoading: boolean;
  // CRM Integration props
  onLogActivity?: (activityData: any) => Promise<void>;
  onComposeEmail?: (recipients: string[], subject?: string) => void;
  // Custom event click handler
  onEventClick?: (event: CalendarEvent) => void;
  // Custom event double-click handler
  onEventDoubleClick?: (event: CalendarEvent) => void;
}

export function AdvancedCalendar({ 
  events, 
  isLoading, 
  onLogActivity, 
  onComposeEmail,
  onEventClick,
  onEventDoubleClick
}: AdvancedCalendarProps) {
  const { 
    currentView, 
    setCurrentView, 
    currentDate, 
    setCurrentDate,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousWeek,
    goToNextWeek,
    goToPreviousDay,
    goToNextDay,
    goToToday
  } = useCalendarContext();
  const sourceColors = useSourceColors();
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<CalendarEvent | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const calendarRef = useRef<FullCalendarHandle>(null);
  const { toast } = useToast();
  const deleteEventMutation = useDeleteEvent();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Dynamic period label based on current view
  const periodLabel = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const weekStartDate = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEndDate = endOfWeek(currentDate, { weekStartsOn: 1 });

    switch (currentView) {
      case "dayGridMonth":
        return format(monthStart, "MMMM yyyy");
      case "timeGridDay":
        return format(currentDate, "EEEE, MMMM d, yyyy");
      case "listWeek":
      case "timeGridWeek":
        return `Week of ${format(weekStartDate, "MMM d")} - ${format(weekEndDate, "MMM d, yyyy")}`;
      default:
        return format(monthStart, "MMMM yyyy");
    }
  }, [currentView, currentDate]);

  // Memoized navigation handlers
  const handlePrevious = useCallback(() => {
    if (currentView === "dayGridMonth") goToPreviousMonth();
    else if (currentView === "timeGridWeek" || currentView === "listWeek") goToPreviousWeek();
    else goToPreviousDay();
  }, [currentView, goToPreviousMonth, goToPreviousWeek, goToPreviousDay]);

  const handleNext = useCallback(() => {
    if (currentView === "dayGridMonth") goToNextMonth();
    else if (currentView === "timeGridWeek" || currentView === "listWeek") goToNextWeek();
    else goToNextDay();
  }, [currentView, goToNextMonth, goToNextWeek, goToNextDay]);

  // Use the current date from context
  const calendarDate = useMemo(() => currentDate, [currentDate]);

  // Force calendar to show current date on initial mount and when currentDate changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(calendarDate);
      
      // Force a re-render by changing the date twice
      setTimeout(() => {
        calendarApi.gotoDate(calendarDate);
      }, 100);
    }
  }, [calendarDate]); // Run when calendarDate changes

  // Handle view changes by updating the FullCalendar instance
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const currentCalendarView = calendarApi.view.type;
      
      // Only change view if it's different from current to avoid unnecessary re-renders
      if (currentCalendarView !== currentView) {
        calendarApi.changeView(currentView);
        
        // Force calendar to show current date when switching views
        if (currentView === 'listWeek') {
          // For list view, start from today's date
          const today = new Date();
          calendarApi.gotoDate(today);
        } else {
          calendarApi.gotoDate(currentDate);
        }
      }
    }
  }, [currentView]);

  // No special container observers; rely on FullCalendar's own resize handling

  // Handle date changes by updating the FullCalendar instance
  useEffect(() => {
    if (calendarRef.current && isInitialized) {
      const calendarApi = calendarRef.current.getApi();
      const calendarDate = calendarApi.getDate();
      
      // Force date sync if months are different, regardless of time difference
      const monthsDifferent = currentDate.getMonth() !== calendarDate.getMonth() || 
                             currentDate.getFullYear() !== calendarDate.getFullYear();
      
      if (monthsDifferent || Math.abs(calendarDate.getTime() - currentDate.getTime()) > 1000) {
        calendarApi.gotoDate(currentDate);
      }
    }
  }, [currentDate, isInitialized]);

  // Month view date synchronization: rely solely on currentDate from context
  // No special forced month logic

  const handleViewChange = (view: CalendarViewType) => {
    setCurrentView(view);
    // The useEffect above will handle the actual calendar view change
  };

  // Add keyboard shortcuts for view switching
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts when not in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case '1':
            event.preventDefault();
            handleViewChange('dayGridMonth');
            break;
          case '2':
            event.preventDefault();
            handleViewChange('timeGridWeek');
            break;
          case '3':
            event.preventDefault();
            handleViewChange('timeGridDay');
            break;
          case '4':
            event.preventDefault();
            handleViewChange('listWeek');
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const getEventColor = (source: string) => {
    // First try to get from sourceColors (which uses CSS variables)
    if (sourceColors[source]) {
      return sourceColors[source];
    }
    
    // Fallback based on source type
    const sourceLower = source.toLowerCase();
    if (sourceLower.includes('google') || sourceLower.includes('goog')) {
      return "oklch(var(--calendar-google))";
    } else if (sourceLower.includes('microsoft') || sourceLower.includes('msft')) {
      return "oklch(var(--calendar-microsoft))";
    } else if (sourceLower.includes('unified')) {
      return "oklch(var(--calendar-unified))";
    } else if (sourceLower === "wtc-email-calendar" || sourceLower === "professional-email-calendar") {
      return "#7c3aed";
    } else {
      return "oklch(var(--calendar-external))";
    }
  };

  // Convert events to FullCalendar format
  const calendarEvents = useMemo(() => {
    let eventsToMap = events;
    
    // Filter to only days with entries if toggle is enabled
    // This logic is now handled by the calendar's own date filtering
    // For month view, we'll hide empty days by filtering events
    // For other views, we'll just show the filtered events
    eventsToMap = events;
    
    // Map events to FullCalendar format with validation
    const mapped = eventsToMap
      .map((event) => {
        // Validate required fields - FullCalendar will drop events without these
        const id = event.id;
        const title = event.title || "Untitled Event";
        const start = event.start;
        const end = event.end;
        
        // Validate dates
        const startDate = start ? new Date(start) : null;
        const endDate = end ? new Date(end) : null;
        const isValidStart = startDate && !isNaN(startDate.getTime());
        const isValidEnd = endDate && !isNaN(endDate.getTime());
        
        // Skip invalid events (FullCalendar will drop them anyway)
        if (!id || !title || !isValidStart || !isValidEnd) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ [FullCalendar] Skipping invalid event:', {
              hasId: !!id,
              hasTitle: !!title,
              hasValidStart: isValidStart,
              hasValidEnd: isValidEnd,
              event: { id, title, start, end, source: event.source },
            });
          }
          return null;
        }
        
        return {
          id,
          title,
          start: startDate.toISOString(), // Ensure ISO string format
          end: endDate.toISOString(), // Ensure ISO string format
          allDay: event.allDay || false,
          backgroundColor: (event as any).backgroundColor || getEventColor(event.source),
          borderColor: (event as any).borderColor || getEventColor(event.source),
          textColor: (event as any).textColor || "#ffffff",
          extendedProps: {
            source: event.source,
            description: event.description,
            location: event.location,
            attendees: event.attendees,
            organizer: event.organizer,
            isOnlineMeeting: event.isOnlineMeeting,
            onlineMeetingUrl: event.onlineMeetingUrl,
            webLink: event.webLink,
            showAs: event.showAs,
            importance: event.importance,
            isCancelled: event.isCancelled,
            originalEvent: event,
          },
        };
      })
      .filter((event): event is NonNullable<typeof event> => event !== null);

    // Dev log: count mapped events per source/calendar after mapping to FullCalendar
    if (process.env.NODE_ENV === 'development') {
      const eventsBySource = mapped.reduce((acc, event) => {
        const source = event.extendedProps.source || 'unknown';
        acc[source] = (acc[source] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      const invalidCount = eventsToMap.length - mapped.length;
      
      console.log('📊 [FullCalendar] Events mapped to FullCalendar format:', {
        totalInput: eventsToMap.length,
        totalMapped: mapped.length,
        invalidDropped: invalidCount,
        eventsBySource,
        currentView,
      });
      
      // Debug: summarize event date range
      if (mapped.length > 0) {
        try {
          const dates = mapped.map(e => new Date(e.start).getTime()).filter(n => !isNaN(n));
          if (dates.length > 0) {
            const min = new Date(Math.min(...dates));
            const max = new Date(Math.max(...dates));
            console.log('🔍 FullCalendar event date range:', {
              minDate: min.toISOString(),
              maxDate: max.toISOString(),
              calendarDate: calendarDate.toISOString(),
            });
          }
        } catch (err) {
          // Ignore date range calculation errors
        }
      } else {
        console.log('🔍 FullCalendar mapped events: none');
      }
    }

    return mapped;
  }, [events, currentView, calendarDate]);

  const handleEventClick = (info: any) => {
    const originalEvent = info.event.extendedProps.originalEvent;
    
    // If custom event click handler provided, use it instead of default modal
    if (onEventClick) {
      onEventClick(originalEvent);
      return;
    }
    
    // Default behavior: open EventDetailsModal
    setSelectedEvent(originalEvent);
    setIsEventModalOpen(true);
  };

  const handleEventDoubleClick = (info: any) => {
    const originalEvent = info.event.extendedProps.originalEvent;
    
    // If custom double-click handler provided, use it instead of default navigation
    if (onEventDoubleClick) {
      onEventDoubleClick(originalEvent);
      return;
    }
    
    // Default behavior: open in new tab
    window.open(`/unifiedtime/event/${originalEvent.id}`, '_blank');
  };

  // Custom event renderer to handle double-click and tooltips
  const eventContent = (arg: any) => {
    const event = arg.event;
    const originalEvent = event.extendedProps.originalEvent;
    const eventTitle = event.title;
    const eventTime = `${format(new Date(event.start), 'h:mm a')} - ${format(new Date(event.end), 'h:mm a')}`;
    const eventLocation = originalEvent?.location || '';
    const eventDescription = originalEvent?.description || '';
    const customMetadata = originalEvent?.customMetadata || {};
    
    // Create tooltip content
    const tooltipContent = (
      <div className="space-y-1">
        <div className="font-semibold text-sm">{eventTitle}</div>
        <div className="text-xs text-muted-foreground">{eventTime}</div>
        
        {/* Display custom metadata fields */}
        {Object.entries(customMetadata).map(([key, value]) => {
          if (!value) return null;
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          return (
            <div key={key} className="text-xs text-muted-foreground">
              <span className="font-medium">{label}:</span> {String(value)}
            </div>
          );
        })}
        
        {eventLocation && <div className="text-xs text-muted-foreground">📍 {eventLocation}</div>}
        {eventDescription && (
          <div className="text-xs text-muted-foreground max-w-xs">
            {eventDescription.length > 100 
              ? `${eventDescription.substring(0, 100)}...` 
              : eventDescription
            }
          </div>
        )}
        <div className="text-xs text-muted-foreground italic">Double-click to open details</div>
      </div>
    );

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className="cursor-pointer w-full h-full flex items-center px-1 hover:opacity-90 transition-opacity"
            onDoubleClick={() => handleEventDoubleClick(arg)}
          >
            <span className="truncate text-xs font-medium leading-tight">
              {eventTitle}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    );
  };

  const handleExportCSV = () => {
    if (events.length === 0) {
      toast({
        title: "No events to export",
        description: "There are no events to export at this time.",
        variant: "destructive",
      });

      return;
    }

    try {
      const csvContent = objectsToCSV(events);
      downloadCSV(csvContent, "calendar-events.csv");
      toast({
        title: "Export successful",
        description: "Calendar events have been exported to CSV.",
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to export calendar events.",
        variant: "destructive",
      });
    }
  };

  const handleEditEvent = (event: CalendarEvent) => {
    // TODO: Implement edit functionality
    toast({
      title: "Edit feature",
      description: "Event editing will be implemented soon.",
    });
  };

  const handleDeleteEvent = (event: CalendarEvent) => {
    setEventToDelete(event);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteEvent = async () => {
    if (!eventToDelete) return;

    try {
      await deleteEventMutation.mutateAsync({
        eventId: eventToDelete.id,
        source: eventToDelete.source,
      });

      toast({
        title: "Event deleted",
        description: "The event has been successfully deleted.",
      });

      // Close the modal
      setIsEventModalOpen(false);
      setSelectedEvent(null);
      
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Failed to delete the event. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setEventToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      {/* FullCalendar */}
      <Card className="overflow-visible">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            {/* Navigation Controls */}
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevious}
                className="h-9 px-3"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToToday}
                className="h-9 px-4"
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                className="h-9 px-3"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Filter Toggle */}
            {/* Removed filter toggle as showOnlyDaysWithEntries state is removed */}
            
            {/* Current Period Display */}
            <div className="flex items-center gap-2 h-9 px-4 bg-muted/50 rounded-md border border-border">
              {currentView === "dayGridMonth" && <CalendarDays className="h-4 w-4 text-muted-foreground" />}
              {(currentView === "timeGridWeek" || currentView === "listWeek") && <CalendarIcon className="h-4 w-4 text-muted-foreground" />}
              {currentView === "timeGridDay" && <Clock className="h-4 w-4 text-muted-foreground" />}
              <span className="text-sm font-semibold text-foreground whitespace-nowrap">
                {periodLabel}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0 overflow-visible">
          <div className="min-h-[500px] min-w-0 w-full">
            <FullCalendar
              key={`calendar-${calendarDate.getFullYear()}-${calendarDate.getMonth()}-${calendarDate.getDate()}-${currentView}`}
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView={currentView}
              initialDate={calendarDate}
              timeZone="local"
              expandRows={true}
              aspectRatio={1.4}
              handleWindowResize={true}
              windowResizeDelay={100}
              height="auto"
              contentHeight="auto"
              headerToolbar={false}
              
              // Month view specific settings
              fixedWeekCount={false}
              showNonCurrentDates={false}
              
              events={calendarEvents}
              eventDidMount={undefined}
              
              eventClick={handleEventClick}
              eventContent={eventContent}
              
              dayMaxEvents={true}
              moreLinkClick="popover"
              eventDisplay="block"
              eventTimeFormat={{
                hour: "numeric",
                minute: "2-digit",
                meridiem: "short",
              }}
              slotMinTime="06:00:00"
              slotMaxTime="22:00:00"
              allDaySlot={true}
              slotDuration="00:30:00"
              slotLabelFormat={{
                hour: "numeric",
                minute: "2-digit",
                meridiem: "short",
              }}
              dayHeaderFormat={currentView === 'dayGridMonth' ? {
                weekday: "short"
              } : {
                weekday: "short",
                month: "short", 
                day: "numeric",
              }}
              firstDay={1}
              listDayFormat={{
                weekday: "long",
                month: "short",
                day: "numeric",
                year: "numeric"
              }}
              listDaySideFormat={{
                weekday: "long",
              }}
              // Handle view changes from FullCalendar itself
              viewDidMount={(info: ViewMountArg) => {
                // Sync the state when view changes from other sources
                setCurrentView(info.view.type as CalendarViewType);
                
                // Force calendar to show appropriate date on mount
                const calendarApi = info.view.calendar;
                if (info.view.type === 'listWeek') {
                  // For list view, start from today
                  const today = new Date();
                  calendarApi.gotoDate(today);
                } else {
                  calendarApi.gotoDate(currentDate);
                }
              }}
              // Handle date changes from FullCalendar navigation
              datesSet={(info: DatesSetArg) => {
                // Mark as initialized after first datesSet call
                if (!isInitialized) {
                  setIsInitialized(true);
                }
                
                // DO NOT update currentDate here - it causes the September bug!
                // The issue: info.start is the START of the visible range (e.g., Sept 29 for October month view)
                // The navigation buttons already update currentDate directly via the context methods
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Event Details Modal */}
      <EventDetailsModal
        event={selectedEvent}
        isOpen={isEventModalOpen}
        onClose={() => {
          setIsEventModalOpen(false);
          setSelectedEvent(null);
        }}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
        onLogActivity={onLogActivity}
        onComposeEmail={onComposeEmail}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="delete-confirmation-modal">
          <AlertDialogHeader className="delete-confirmation-header">
            <AlertDialogTitle className="delete-confirmation-title">
              🗑️ Delete Event
            </AlertDialogTitle>
            <AlertDialogDescription className="delete-confirmation-description">
              Are you sure you want to delete <strong>"{eventToDelete?.title}"</strong>?
            </AlertDialogDescription>
            <div className="delete-warning-text">
              ⚠️ This action cannot be undone
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="delete-confirmation-footer">
            <AlertDialogCancel 
              disabled={deleteEventMutation.isPending}
              className="delete-confirmation-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteEvent}
              disabled={deleteEventMutation.isPending}
              className="delete-confirmation-delete"
            >
              {deleteEventMutation.isPending ? "Deleting..." : "Delete Event"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
} 